import en from './en.json';

type Params = Record<string, string | number>;
type Dictionary = Record<string, string>;
type RuntimeTranslations = Dictionary | Record<string, Dictionary>;
type InitI18nOptions = {
  locale?: string;
  runtimeTranslations?: RuntimeTranslations;
};

const DEFAULT_LOCALE = 'en';
const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur']);
const STORAGE_KEY = 'app.locale';

const dictionaries: Record<string, Dictionary> = {
  en: en as Dictionary,
};
let runtimeDictionaries: Record<string, Dictionary> = {};
const localeLoaders = import.meta.glob<{ default: Dictionary }>([
  './*.json',
  '!./en.json',
]);
const localeLoadPromises: Record<string, Promise<Dictionary>> = {};

let currentLocale = DEFAULT_LOCALE;

const interpolate = (template: string, params?: Params) => {
  if (!params) return template;

  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const value = params[key];
    return value === undefined ? '' : String(value);
  });
};

const normalizeLocale = (locale?: string | null) => {
  if (!locale) return DEFAULT_LOCALE;
  return locale.toLowerCase().split('-')[0] || DEFAULT_LOCALE;
};

const applyDocumentLocale = (locale: string) => {
  if (typeof document === 'undefined') return;
  const lang = normalizeLocale(locale);
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
};

const isDictionary = (value: unknown): value is Dictionary =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const normalizeRuntimeTranslations = (
  translations?: RuntimeTranslations,
  locale?: string
): Record<string, Dictionary> => {
  if (!translations) return {};

  if (locale) {
    return { [normalizeLocale(locale)]: translations as Dictionary };
  }

  const entries = Object.entries(translations);
  if (entries.every(([, value]) => isDictionary(value))) {
    return Object.fromEntries(
      entries.map(([key, value]) => [normalizeLocale(key), value as Dictionary])
    );
  }

  return { [DEFAULT_LOCALE]: translations as Dictionary };
};

const resolveInitialLocale = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved) return normalizeLocale(saved);

  return normalizeLocale(window.navigator.language);
};

const getLocaleLoader = (locale: string) => localeLoaders[`./${locale}.json`];

const loadLocale = async (locale: string) => {
  const normalizedLocale = normalizeLocale(locale);
  if (dictionaries[normalizedLocale]) return dictionaries[normalizedLocale];

  if (!localeLoadPromises[normalizedLocale]) {
    const loader = getLocaleLoader(normalizedLocale);
    localeLoadPromises[normalizedLocale] = loader
      ? loader()
          .then((module) => {
            dictionaries[normalizedLocale] = module.default;
            return module.default;
          })
          .catch(() => dictionaries[DEFAULT_LOCALE])
      : Promise.resolve(dictionaries[DEFAULT_LOCALE]);
  }

  return localeLoadPromises[normalizedLocale];
};

const setLocale = async (locale: string) => {
  currentLocale = normalizeLocale(locale);
  await loadLocale(currentLocale);
  applyDocumentLocale(currentLocale);

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, currentLocale);
  }
};

export const getLocale = () => currentLocale;

const registerRuntimeTranslations = (
  translations?: RuntimeTranslations,
  locale?: string
) => {
  runtimeDictionaries = normalizeRuntimeTranslations(translations, locale);
};

export const initI18n = (localeOrOptions?: string | InitI18nOptions) => {
  const options =
    typeof localeOrOptions === 'string'
      ? { locale: localeOrOptions }
      : localeOrOptions || {};

  registerRuntimeTranslations(options.runtimeTranslations, options.locale);
  return setLocale(options.locale || resolveInitialLocale());
};

const rawTemplate = (key: string) => {
  const dictionary = dictionaries[currentLocale] || dictionaries[DEFAULT_LOCALE];
  const runtimeTranslations = runtimeDictionaries[currentLocale];
  return runtimeTranslations?.[key] ?? dictionary[key] ?? key;
};

export const _t = (key: string, params?: Params) => interpolate(rawTemplate(key), params);

// Splits a translation on its {{placeholder}} tokens so callers can render
// individual params as JSX (e.g. bolding a number) instead of plain text.
export const _tParts = (key: string, params: Params): Array<string | { key: string; value: string }> =>
  rawTemplate(key)
    .split(/\{\{(\w+)\}\}/g)
    .map((segment, i) => (i % 2 === 1 ? { key: segment, value: String(params[segment] ?? '') } : segment))
    .filter((segment) => segment !== '');

export default _t;
